import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { MapPin, Clock, Users, Euro, ArrowRight, Star, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import SEOHead, { organizationSchema } from "@/components/SEOHead";
import LocalizedLink from "@/components/LocalizedLink";

const API = process.env.REACT_APP_BACKEND_URL;

// City data with SEO content
const cityData = {
  marrakech: {
    name: { fr: "Marrakech", en: "Marrakech", es: "Marrakech", nl: "Marrakech", ar: "مراكش" },
    slug: "marrakech",
    image: "https://images.unsplash.com/photo-1597212618440-806262de4f6b?w=1200",
    seo: {
      fr: {
        title: "Trek & Circuit 4x4 Départ Marrakech | Désert Sahara Maroc - XploreSahara",
        description: "Trek désert Maroc et circuits 4x4 au départ de Marrakech. Direction Erg Chegaga, Erg Zahar, M'Hamid via Ouarzazate et la vallée du Draa. Trek 3, 4, 5, 7 jours. Bivouac nomade sous les étoiles.",
        keywords: ["trek départ Marrakech", "circuit 4x4 Marrakech désert", "excursion Sahara Marrakech", "voyage désert depuis Marrakech"]
      },
      en: {
        title: "Trek & 4x4 Tour from Marrakech | Sahara Desert Morocco - XploreSahara",
        description: "Morocco desert trek and 4x4 tours from Marrakech. To Erg Chegaga, Erg Zahar, M'Hamid via Ouarzazate and Draa valley. 3, 4, 5, 7 day treks. Nomad camping under the stars.",
        keywords: ["trek from Marrakech", "4x4 tour Marrakech desert", "Sahara excursion Marrakech", "desert trip from Marrakech"]
      }
    },
    intro: {
      fr: "Marrakech, la ville rouge, est le point de départ idéal pour votre aventure dans le désert du Sahara. À seulement 6-7 heures de route, découvrez les majestueuses dunes d'Erg Chegaga en traversant le col du Tizi n'Tichka, Ouarzazate, la vallée du Draa et ses palmeraies millénaires.",
      en: "Marrakech, the red city, is the ideal starting point for your Sahara desert adventure. Just 6-7 hours away, discover the majestic Erg Chegaga dunes crossing the Tizi n'Tichka pass, Ouarzazate, the Draa valley and its ancient palm groves."
    },
    highlights: {
      fr: ["Col du Tizi n'Tichka (2260m)", "Aït Ben Haddou (UNESCO)", "Ouarzazate - Hollywood du Maroc", "Vallée du Draa", "Dunes d'Erg Chegaga"],
      en: ["Tizi n'Tichka Pass (2260m)", "Aït Ben Haddou (UNESCO)", "Ouarzazate - Hollywood of Morocco", "Draa Valley", "Erg Chegaga Dunes"]
    },
    distance: "360 km"
  },
  ouarzazate: {
    name: { fr: "Ouarzazate", en: "Ouarzazate", es: "Ouarzazate", nl: "Ouarzazate", ar: "ورزازات" },
    slug: "ouarzazate",
    image: "https://images.unsplash.com/photo-1489749798305-4fea3ae63d43?w=1200",
    seo: {
      fr: {
        title: "Trek & Circuit 4x4 Départ Ouarzazate | Désert Sahara - XploreSahara",
        description: "Treks et circuits 4x4 au départ de Ouarzazate vers le désert. Kasbahs, vallée du Draa, M'Hamid, Erg Chegaga. Circuits courts et longs. Bivouac authentique.",
        keywords: ["trek départ Ouarzazate", "circuit 4x4 Ouarzazate", "excursion désert Ouarzazate", "voyage Sahara Ouarzazate"]
      },
      en: {
        title: "Trek & 4x4 Tour from Ouarzazate | Sahara Desert - XploreSahara",
        description: "Treks and 4x4 tours from Ouarzazate to the desert. Kasbahs, Draa valley, M'Hamid, Erg Chegaga. Short and long tours. Authentic camping.",
        keywords: ["trek from Ouarzazate", "4x4 tour Ouarzazate", "desert excursion Ouarzazate", "Sahara trip Ouarzazate"]
      }
    },
    intro: {
      fr: "Ouarzazate, la porte du désert, vous rapproche considérablement du Sahara. Cette ville de cinéma, célèbre pour ses studios, est le carrefour parfait entre l'Atlas et le désert. D'ici, rejoignez rapidement M'Hamid et les dunes d'Erg Chegaga.",
      en: "Ouarzazate, the gateway to the desert, brings you much closer to the Sahara. This cinema city, famous for its studios, is the perfect crossroads between the Atlas and the desert. From here, quickly reach M'Hamid and the Erg Chegaga dunes."
    },
    highlights: {
      fr: ["Studios de cinéma Atlas", "Kasbah Taourirt", "Vallée du Draa", "Zagora", "M'Hamid El Ghizlane"],
      en: ["Atlas Film Studios", "Taourirt Kasbah", "Draa Valley", "Zagora", "M'Hamid El Ghizlane"]
    },
    distance: "160 km"
  },
  mhamid: {
    name: { fr: "M'Hamid El Ghizlane", en: "M'Hamid El Ghizlane", es: "M'Hamid El Ghizlane", nl: "M'Hamid El Ghizlane", ar: "محاميد الغزلان" },
    slug: "mhamid",
    image: "https://images.unsplash.com/photo-1548786811-dd6e453ccca7?w=1200",
    seo: {
      fr: {
        title: "Trek & Circuit 4x4 M'Hamid | Porte du Désert Sahara Maroc - XploreSahara",
        description: "M'Hamid El Ghizlane, dernière oasis avant le Sahara. Accès direct aux dunes d'Erg Chegaga et Erg Zahar. Trek nomade authentique avec les habitants locaux. Bivouac traditionnel.",
        keywords: ["trek M'Hamid", "circuit 4x4 M'Hamid", "Erg Chegaga M'Hamid", "désert M'Hamid", "porte du désert"]
      },
      en: {
        title: "Trek & 4x4 Tour M'Hamid | Gateway to Sahara Desert Morocco - XploreSahara",
        description: "M'Hamid El Ghizlane, last oasis before the Sahara. Direct access to Erg Chegaga and Erg Zahar dunes. Authentic nomad trek with local people. Traditional camping.",
        keywords: ["trek M'Hamid", "4x4 tour M'Hamid", "Erg Chegaga M'Hamid", "M'Hamid desert", "gateway to desert"]
      }
    },
    intro: {
      fr: "M'Hamid El Ghizlane, la 'plaine des gazelles', est la dernière oasis avant l'immensité du Sahara. C'est ici que vivent nos guides, au cœur du désert. Point de départ privilégié pour les treks authentiques vers Erg Chegaga et Erg Zahar.",
      en: "M'Hamid El Ghizlane, the 'plain of gazelles', is the last oasis before the vastness of the Sahara. This is where our guides live, in the heart of the desert. Privileged starting point for authentic treks to Erg Chegaga and Erg Zahar."
    },
    highlights: {
      fr: ["Erg Chegaga (plus hautes dunes)", "Erg Zahar", "Lac Iriki", "Oasis de palmiers", "Vie nomade authentique"],
      en: ["Erg Chegaga (highest dunes)", "Erg Zahar", "Lake Iriki", "Palm oasis", "Authentic nomad life"]
    },
    distance: "0 km - Point de départ"
  },
  zagora: {
    name: { fr: "Zagora", en: "Zagora", es: "Zagora", nl: "Zagora", ar: "زاكورة" },
    slug: "zagora",
    image: "https://images.unsplash.com/photo-1531219432768-9f540ce91ef3?w=1200",
    seo: {
      fr: {
        title: "Trek & Circuit 4x4 Départ Zagora | Vallée du Draa Sahara - XploreSahara",
        description: "Treks et circuits 4x4 au départ de Zagora. Célèbre panneau '52 jours à Tombouctou'. Vallée du Draa, palmeraies, désert du Sahara. Excursions et séjours.",
        keywords: ["trek départ Zagora", "circuit 4x4 Zagora", "vallée Draa Zagora", "Tombouctou 52 jours"]
      },
      en: {
        title: "Trek & 4x4 Tour from Zagora | Draa Valley Sahara - XploreSahara",
        description: "Treks and 4x4 tours from Zagora. Famous '52 days to Timbuktu' sign. Draa valley, palm groves, Sahara desert. Day trips and stays.",
        keywords: ["trek from Zagora", "4x4 tour Zagora", "Draa valley Zagora", "Timbuktu 52 days"]
      }
    },
    intro: {
      fr: "Zagora, avec son célèbre panneau '52 jours à Tombouctou', marque l'entrée dans le grand Sud marocain. Au cœur de la vallée du Draa, la plus grande palmeraie du Maroc, cette ville est idéale pour découvrir le pré-désert et ses oasis.",
      en: "Zagora, with its famous '52 days to Timbuktu' sign, marks the entrance to the great Moroccan South. In the heart of the Draa valley, Morocco's largest palm grove, this city is ideal for discovering the pre-desert and its oases."
    },
    highlights: {
      fr: ["Panneau Tombouctou 52 jours", "Plus grande palmeraie du Maroc", "Djebel Zagora", "Tamegroute (poteries)", "Porte du Draa"],
      en: ["Timbuktu 52 days sign", "Morocco's largest palm grove", "Jebel Zagora", "Tamegroute (pottery)", "Gateway to Draa"]
    },
    distance: "95 km"
  }
};

const DepartureCityPage = () => {
  const { city } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [circuits, setCircuits] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentLang = i18n.language || 'fr';

  const cityInfo = cityData[city];

  useEffect(() => {
    if (!cityInfo) {
      navigate(`/${currentLang}/circuits`);
      return;
    }
    fetchCircuits();
  }, [city, cityInfo]);

  const fetchCircuits = async () => {
    try {
      const res = await axios.get(`${API}/api/circuits`);
      // Filter circuits that depart from this city
      const filtered = res.data.filter(c => 
        c.departure_city?.toLowerCase().includes(city) || 
        city === 'marrakech' // Marrakech is default departure for most
      );
      setCircuits(filtered.slice(0, 6));
    } catch (error) {
      console.error("Error fetching circuits:", error);
    } finally {
      setLoading(false);
    }
  };

  if (!cityInfo) return null;

  const seoData = cityInfo.seo[currentLang] || cityInfo.seo.fr;

  return (
    <div className="min-h-screen bg-background">
      <SEOHead 
        customTitle={seoData.title}
        customDescription={seoData.description}
        customKeywords={seoData.keywords}
        structuredData={organizationSchema}
      />

      {/* Hero Section */}
      <section className="relative h-[50vh] min-h-[400px]">
        <div className="absolute inset-0">
          <img 
            src={cityInfo.image} 
            alt={`Trek désert départ ${cityInfo.name[currentLang]}`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        </div>
        <div className="relative z-10 h-full flex items-end pb-12">
          <div className="container mx-auto px-4">
            <Badge className="mb-4 bg-primary/90">
              <MapPin className="w-3 h-3 mr-1" />
              {currentLang === 'fr' ? 'Point de départ' : 'Departure point'}
            </Badge>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-white mb-4">
              {currentLang === 'fr' ? 'Trek & Circuit 4x4' : 'Trek & 4x4 Tour'}
              <br />
              <span className="text-primary">{currentLang === 'fr' ? 'Départ' : 'From'} {cityInfo.name[currentLang]}</span>
            </h1>
            <p className="text-xl text-white/90 max-w-2xl">
              {cityInfo.intro[currentLang] || cityInfo.intro.fr}
            </p>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-12 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-card p-6 rounded-xl border">
              <MapPin className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-semibold mb-1">{currentLang === 'fr' ? 'Distance du désert' : 'Distance to desert'}</h3>
              <p className="text-muted-foreground">{cityInfo.distance}</p>
            </div>
            <div className="bg-card p-6 rounded-xl border">
              <Clock className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-semibold mb-1">{currentLang === 'fr' ? 'Durées disponibles' : 'Available durations'}</h3>
              <p className="text-muted-foreground">2 - 13 {currentLang === 'fr' ? 'jours' : 'days'}</p>
            </div>
            <div className="bg-card p-6 rounded-xl border">
              <Users className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-semibold mb-1">{currentLang === 'fr' ? 'Type de voyage' : 'Trip type'}</h3>
              <p className="text-muted-foreground">{currentLang === 'fr' ? 'Privé ou petit groupe' : 'Private or small group'}</p>
            </div>
            <div className="bg-card p-6 rounded-xl border">
              <Star className="w-8 h-8 text-primary mb-3" />
              <h3 className="font-semibold mb-1">{currentLang === 'fr' ? 'Points forts' : 'Highlights'}</h3>
              <p className="text-muted-foreground">{(cityInfo.highlights[currentLang] || cityInfo.highlights.fr)[0]}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-serif font-bold mb-8">
            {currentLang === 'fr' ? `Ce que vous verrez au départ de ${cityInfo.name[currentLang]}` : `What you'll see from ${cityInfo.name[currentLang]}`}
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4">
            {(cityInfo.highlights[currentLang] || cityInfo.highlights.fr).map((highlight, idx) => (
              <div key={idx} className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="font-medium">{highlight}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Circuits */}
      <section className="py-16 bg-muted/30">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-serif font-bold mb-8">
            {currentLang === 'fr' ? `Nos circuits au départ de ${cityInfo.name[currentLang]}` : `Our tours from ${cityInfo.name[currentLang]}`}
          </h2>
          
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
            </div>
          ) : circuits.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {circuits.map((circuit) => (
                <LocalizedLink 
                  key={circuit.id} 
                  to={`/circuit/${circuit.id}`}
                  className="group bg-card rounded-xl overflow-hidden border hover:shadow-lg transition-shadow"
                >
                  <div className="aspect-[16/10] overflow-hidden">
                    <img 
                      src={circuit.image_url} 
                      alt={circuit.title?.[currentLang] || circuit.title?.fr}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-5">
                    <Badge variant="outline" className="mb-2">
                      {circuit.type === '4x4' ? 'Circuit 4x4' : 'Trek'}
                    </Badge>
                    <h3 className="font-semibold text-lg mb-2 group-hover:text-primary transition-colors">
                      {circuit.title?.[currentLang] || circuit.title?.fr}
                    </h3>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>{circuit.duration_days} {currentLang === 'fr' ? 'jours' : 'days'}</span>
                      <span className="font-semibold text-primary">€{circuit.price}</span>
                    </div>
                  </div>
                </LocalizedLink>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              {currentLang === 'fr' ? 'Contactez-nous pour un circuit personnalisé' : 'Contact us for a custom tour'}
            </p>
          )}

          <div className="text-center mt-8">
            <LocalizedLink to="/circuits">
              <Button size="lg" className="gap-2">
                {currentLang === 'fr' ? 'Voir tous nos circuits' : 'View all our tours'}
                <ArrowRight className="w-4 h-4" />
              </Button>
            </LocalizedLink>
          </div>
        </div>
      </section>

      {/* SEO Content Block */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto prose prose-lg">
            <h2 className="text-3xl font-serif font-bold mb-6">
              {currentLang === 'fr' 
                ? `Pourquoi partir de ${cityInfo.name[currentLang]} pour votre trek désert Maroc ?`
                : `Why start from ${cityInfo.name[currentLang]} for your Morocco desert trek?`
              }
            </h2>
            <p className="text-muted-foreground leading-relaxed">
              {currentLang === 'fr' ? (
                <>
                  {cityInfo.name.fr} offre un accès privilégié aux plus belles dunes du Sahara marocain. 
                  Que vous choisissiez un <strong>trek désert</strong> avec dromadaires ou un <strong>circuit 4x4</strong>, 
                  nos guides locaux vous feront découvrir des paysages à couper le souffle : 
                  les majestueuses dunes d'<strong>Erg Chegaga</strong>, les oasis cachées, 
                  les bivouacs traditionnels sous les étoiles.
                  <br /><br />
                  Nos <strong>treks de 3, 4, 5 ou 7 jours</strong> sont conçus pour une immersion totale 
                  dans la vie nomade. <strong>Randonnée désert Maroc</strong> authentique, 
                  <strong>retraite spirituelle</strong> au cœur du Sahara, ou simple <strong>excursion 4x4</strong> : 
                  nous adaptons chaque voyage à vos envies.
                </>
              ) : (
                <>
                  {cityInfo.name.en} offers privileged access to the most beautiful dunes of the Moroccan Sahara.
                  Whether you choose a <strong>desert trek</strong> with camels or a <strong>4x4 tour</strong>,
                  our local guides will show you breathtaking landscapes:
                  the majestic <strong>Erg Chegaga</strong> dunes, hidden oases,
                  traditional camps under the stars.
                  <br /><br />
                  Our <strong>3, 4, 5 or 7 day treks</strong> are designed for total immersion
                  in nomad life. Authentic <strong>Morocco desert hiking</strong>,
                  <strong>spiritual retreat</strong> in the heart of the Sahara, or simple <strong>4x4 excursion</strong>:
                  we adapt each trip to your desires.
                </>
              )}
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-serif font-bold mb-4">
            {currentLang === 'fr' 
              ? `Prêt pour votre aventure au départ de ${cityInfo.name[currentLang]} ?`
              : `Ready for your adventure from ${cityInfo.name[currentLang]}?`
            }
          </h2>
          <p className="text-lg opacity-90 mb-8 max-w-2xl mx-auto">
            {currentLang === 'fr'
              ? 'Contactez-nous pour un devis gratuit et personnalisé. Nos experts du désert vous conseillent.'
              : 'Contact us for a free personalized quote. Our desert experts will advise you.'
            }
          </p>
          <LocalizedLink to="/contact">
            <Button size="lg" variant="secondary" className="gap-2">
              {currentLang === 'fr' ? 'Demander un devis gratuit' : 'Request a free quote'}
              <ArrowRight className="w-4 h-4" />
            </Button>
          </LocalizedLink>
        </div>
      </section>
    </div>
  );
};

export default DepartureCityPage;
